'use client';

import React, { createContext, useContext, useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipContextValue {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  triggerElement: HTMLElement | null;
  setTriggerElement: (element: HTMLElement | null) => void;
}

const TooltipContext = createContext<TooltipContextValue | undefined>(undefined);

/**
 * Tooltip Provider - Context를 제공하여 Tooltip 컴포넌트들 간의 상태 공유
 */
export const TooltipProvider = ({ children }: { children: ReactNode }) => {
  return <>{children}</>;
};

/**
 * Tooltip Root - 개별 툴팁의 상태를 관리
 */
export const Tooltip = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);

  return (
    <TooltipContext.Provider value={{ isOpen, setIsOpen, triggerElement, setTriggerElement }}>
      {children}
    </TooltipContext.Provider>
  );
};

/**
 * Tooltip Trigger - 마우스 오버 시 툴팁을 표시
 * children은 반드시 interactive element(button, link 등)여야 합니다
 */
export const TooltipTrigger = ({ children }: { children: ReactNode }) => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('TooltipTrigger must be used within a Tooltip');
  }

  const { setIsOpen, setTriggerElement } = context;
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    // 이전 지연된 닫기 취소
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    // 툴팁 표시 지연 (500ms) - 빠르게 마우스를 이동할 때는 툴팁이 표시되지 않도록
    enterTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
      enterTimeoutRef.current = null;
    }, 500);
  };

  const handleMouseLeave = () => {
    // 이전 지연된 열기 취소
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    // 깜빡거림 방지를 위해 약간의 지연 추가
    leaveTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      leaveTimeoutRef.current = null;
    }, 100);
  };

  // cleanup 함수
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
      }
      if (enterTimeoutRef.current) {
        clearTimeout(enterTimeoutRef.current);
      }
    };
  }, []);

  // children이 React element인 경우 props를 추가
  if (React.isValidElement(children)) {
    return (
      <>
        {React.cloneElement(children, {
          ref: (node: HTMLElement | null) => {
            setTriggerElement(node);
            // 원래 ref가 있다면 호출
            const originalRef = (children as { ref?: unknown }).ref;
            if (typeof originalRef === 'function') {
              originalRef(node);
            } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
              (originalRef as React.MutableRefObject<HTMLElement | null>).current = node;
            }
          },
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        } as Record<string, unknown>)}
      </>
    );
  }

  return <>{children}</>;
};

interface TooltipContentProps {
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  className?: string;
}

/**
 * 스크롤 가능한 모든 부모 요소를 찾는 함수
 */
const findScrollableParents = (element: HTMLElement): HTMLElement[] => {
  const scrollableParents: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflow = style.overflow + style.overflowY + style.overflowX;

    if (overflow.includes('auto') || overflow.includes('scroll')) {
      scrollableParents.push(parent);
    }

    parent = parent.parentElement;
  }

  return scrollableParents;
};

/**
 * 화면 경계를 벗어나지 않도록 위치를 조정하는 함수
 */
const adjustPositionForBoundary = (
  top: number,
  left: number,
  tooltipWidth: number,
  tooltipHeight: number
): { top: number; left: number } => {
  const padding = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollX = window.scrollX || window.pageXOffset;
  const scrollY = window.scrollY || window.pageYOffset;

  // 화면 좌표로 변환
  const screenLeft = left - scrollX;
  const screenTop = top - scrollY;

  // 수평 경계 체크
  if (screenLeft < padding) {
    left = scrollX + padding;
  } else if (screenLeft + tooltipWidth > viewportWidth - padding) {
    left = scrollX + viewportWidth - tooltipWidth - padding;
  }

  // 수직 경계 체크
  if (screenTop < padding) {
    top = scrollY + padding;
  } else if (screenTop + tooltipHeight > viewportHeight - padding) {
    top = scrollY + viewportHeight - tooltipHeight - padding;
  }

  return { top, left };
};

/**
 * Tooltip Content - 툴팁 내용을 표시 (포털 사용)
 */
export const TooltipContent = ({
  children,
  side = 'bottom',
  align = 'center',
  className = ''
}: TooltipContentProps) => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('TooltipContent must be used within a Tooltip');
  }

  const { isOpen, triggerElement } = context;
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const scrollableParentsRef = useRef<HTMLElement[]>([]);
  const rafIdRef = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerElement || !tooltipRef.current) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    // 툴팁 크기가 0이면 위치 계산 건너뛰기
    if (tooltipRect.width === 0 || tooltipRect.height === 0) {
      return;
    }

    // getBoundingClientRect()는 뷰포트 기준 좌표를 반환하므로
    // window 스크롤 오프셋을 더하여 절대 좌표로 변환
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    let top = 0;
    let left = 0;

    // side에 따른 기본 위치 계산
    switch (side) {
      case 'top':
        top = triggerRect.top + scrollY - tooltipRect.height - 8;
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollY + 8;
        break;
      case 'left':
        top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left + scrollX - tooltipRect.width - 8;
        break;
      case 'right':
        top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + scrollX + 8;
        break;
    }

    // align에 따른 위치 조정
    if (side === 'top' || side === 'bottom') {
      switch (align) {
        case 'start':
          left = triggerRect.left + scrollX;
          break;
        case 'center':
          left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'end':
          left = triggerRect.right + scrollX - tooltipRect.width;
          break;
      }
    } else {
      // left/right일 때는 수직 정렬
      switch (align) {
        case 'start':
          top = triggerRect.top + scrollY;
          break;
        case 'center':
          top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
          break;
        case 'end':
          top = triggerRect.bottom + scrollY - tooltipRect.height;
          break;
      }
    }

    // 화면 경계 체크 및 위치 조정
    const adjustedPosition = adjustPositionForBoundary(
      top,
      left,
      tooltipRect.width,
      tooltipRect.height
    );

    setPosition(adjustedPosition);
    setIsPositionCalculated(true);
  }, [side, align, triggerElement]);

  // requestAnimationFrame을 사용한 위치 업데이트
  const scheduleUpdate = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      updatePosition();
      rafIdRef.current = null;
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!isOpen || !triggerElement) {
      setIsPositionCalculated(false);
      return;
    }

    // 위치 계산 상태 초기화
    setIsPositionCalculated(false);

    // 스크롤 가능한 부모 요소 찾기
    scrollableParentsRef.current = findScrollableParents(triggerElement);

    // 초기 위치 계산 (약간의 지연을 두어 DOM이 완전히 렌더링된 후)
    const timeoutId = setTimeout(() => {
      scheduleUpdate();
    }, 0);

    // ResizeObserver를 사용하여 툴팁 크기 변경 감지
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });

    if (tooltipRef.current) {
      resizeObserver.observe(tooltipRef.current);
    }

    // 스크롤 이벤트 리스너 추가
    const handleScroll = () => {
      scheduleUpdate();
    };

    // window 스크롤 이벤트 (capture phase에서 처리하여 모든 스크롤 이벤트 감지)
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    // 스크롤 가능한 부모 요소들의 스크롤 이벤트
    scrollableParentsRef.current.forEach(parent => {
      parent.addEventListener('scroll', handleScroll, true);
    });

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
      scrollableParentsRef.current.forEach(parent => {
        parent.removeEventListener('scroll', handleScroll, true);
      });
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [isOpen, triggerElement, scheduleUpdate]);

  if (!isOpen) return null;

  // 화살표 위치
  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-1',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-1',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-1',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-1',
  };

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`fixed z-[9999] px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap animate-in fade-in-0 zoom-in-95 ${className}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: isPositionCalculated ? 'visible' : 'hidden',
      }}
      role="tooltip"
    >
      {children}
      <div className={`absolute w-2 h-2 bg-gray-900 rotate-45 ${arrowClasses[side]}`} />
    </div>
  );

  // 포털을 사용하여 body에 직접 렌더링
  if (typeof window === 'undefined') return null;
  return createPortal(tooltipContent, document.body);
};

/**
 * 간편하게 사용할 수 있는 SimpleTooltip 컴포넌트
 * children은 반드시 interactive element(button, link 등)여야 합니다
 */
interface SimpleTooltipProps {
  content: string;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

export const SimpleTooltip = ({ content, children, side = 'bottom', align = 'center' }: SimpleTooltipProps) => {
  return (
    <Tooltip>
      <TooltipTrigger>
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} align={align}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};
